import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Minus, Trash2, ShoppingCart, CreditCard, DollarSign, X, Package, LayoutGrid, Clock, ScanLine, Search, UserPlus, User } from 'lucide-react';
import API_URL from '../../config/api';
import { ProductOptionsPicker } from './OpcionesPlato';
import ReciboManager from './ReciboManager';
import CierreCaja from './CierreCaja';
import CustomAlert from './shared/CustomAlert';

const CajaVentas = ({ token, onSaleComplete, onNavigateTo, clientData }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [cart, setCart] = useState([]);
  // ⭐ Opciones de plato (2026-08-09): cuando el producto tocado tiene grupos
  // configurados, se abre el selector ANTES de agregarlo al carrito. `null`
  // significa que no hay ningún plato esperando elección.
  const [optionsFor, setOptionsFor] = useState(null);
  const [productsWithOptions, setProductsWithOptions] = useState(() => new Set());
  // ⭐ Sin categoría por defecto (antes 'all' precargaba todo el catálogo al
  // abrir Caja). Ahora el cajero elige explícitamente qué categoría ver con
  // el botón/selector de abajo — evita cargar productos que no necesita en
  // ese momento y hace más obvio qué está viendo.
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [orderType, setOrderType] = useState('servir'); // 'servir' | 'llevar'
  const [customerName, setCustomerName] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [showChange, setShowChange] = useState(false);
  const [customAlert, setCustomAlert] = useState(null);
  const [savingPending, setSavingPending] = useState(false);
  const [showTablesModal, setShowTablesModal] = useState(null); // null | 'available' | 'occupied'
  const [pendingReceipt, setPendingReceipt] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanningBarcode, setScanningBarcode] = useState(false);

  // ⭐ Cliente registrado (búsqueda por cédula/nombre + registro rápido) y
  // tipo de comprobante. selectedCustomer viaja en la venta como customer_id
  // + billing_data (snapshot de sus datos al momento de facturar, para que
  // el recibo/factura no cambie si luego se edita el cliente).
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showRegisterCustomer, setShowRegisterCustomer] = useState(false);
  const [registerForm, setRegisterForm] = useState({ first_name: '', last_name: '', cedula: '', phone: '', sector: '', email: '', document_type: '05', legal_name: '', fiscal_address: '' });
  const [registeringCustomer, setRegisteringCustomer] = useState(false);
  const [billingType, setBillingType] = useState('consumidor_final'); // 'consumidor_final' | 'factura'

  // ⭐ Bloqueo de venta sin caja abierta (configurable desde Configuración,
  // ver /api/pos/settings). `requireOpenCashSession` es la política de la
  // cuenta; `cashSessionOpen` es el estado real ahora mismo, que reporta
  // CierreCaja.js vía onSessionChange (misma fuente de datos que ya usa el
  // botón "Caja Abierta/Cerrada", sin duplicar el polling). Mientras
  // cualquiera de los dos todavía no se conoce (null = cargando), NO se
  // bloquea -- evita un falso "caja cerrada" parpadeando al entrar a Caja.
  const [requireOpenCashSession, setRequireOpenCashSession] = useState(false);
  const [cashSessionOpen, setCashSessionOpen] = useState(null);

  // ⭐ Se pide UNA lista de ids, no las opciones de cada plato: la grilla puede
  // tener cientos de productos y solo hace falta saber cuáles abren el selector.
  // El detalle se pide recién al tocar el plato.
  useEffect(() => {
    axios.get(`${API_URL}/api/pos/products-with-options`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setProductsWithOptions(new Set(res.data || [])))
      .catch(() => setProductsWithOptions(new Set()));
  }, [token]);

  useEffect(() => {
    axios.get(`${API_URL}/api/pos/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setRequireOpenCashSession(!!res.data?.require_open_cash_session))
      .catch(() => {}); // si falla, se asume desactivado (comportamiento actual, no se bloquea nada)
  }, [token]);

  // ⭐ Búsqueda + paginación de productos (plan de escalabilidad §3.2/3.3):
  // ya no se precarga todo el catálogo. Se pide por página/categoría/texto
  // al servidor, con debounce para no disparar una petición por cada tecla.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [productsPage, setProductsPage] = useState(1);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsHasMore, setProductsHasMore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const PRODUCTS_PAGE_SIZE = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 280);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Búsqueda de cliente (cédula o nombre) con debounce, igual patrón que
  // la búsqueda de productos.
  useEffect(() => {
    const term = customerSearchTerm.trim();
    if (!term) {
      setCustomerSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchingCustomer(true);
      try {
        const res = await axios.get(`${API_URL}/api/pos/customers/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: term }
        });
        setCustomerSearchResults(res.data || []);
      } catch (error) {
        console.error('Error al buscar cliente:', error);
      } finally {
        setSearchingCustomer(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [customerSearchTerm, token]);

  useEffect(() => {
    loadCategories();
    loadTables();
    // Auto-refresh tables every 5s so occupied status is up-to-date from other tabs/devices
    const interval = setInterval(loadTables, 5000);
    return () => clearInterval(interval);
  }, []);

  // Categoría o búsqueda cambiaron → volver a página 1 y reemplazar la lista.
  // Sin categoría seleccionada y sin búsqueda activa, no se pide nada al
  // servidor: se deja la grilla vacía con el mensaje de "elige categoría"
  // (ver JSX más abajo) en vez de traer todo el catálogo por defecto.
  useEffect(() => {
    if (!selectedCategory && !debouncedSearch) {
      setProducts([]);
      setProductsTotal(0);
      setProductsHasMore(false);
      setLoadingProducts(false);
      return;
    }
    loadProducts({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, debouncedSearch]);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.map(cat => cat.name));
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTables(response.data || []);
    } catch (error) {
      console.error('Error al cargar mesas:', error);
    }
  };

  const saveAsPending = async () => {
    if (cart.length === 0) {
      showAlert('warning', 'Carrito vacío', 'Agrega productos antes de guardar el pedido.');
      return;
    }
    setSavingPending(true);
    try {
      const saleData = {
        total: getTotal(),
        payment_method: null,
        payment_status: 'pending',
        order_type: orderType,
        customer_name: customerName.trim(),
        table_id: selectedTable?.id || null,
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: getItemPrice(item),
          quantity: item.quantity,
          use_stock: item.use_stock,
          // ⭐ El backend revalida esto y recalcula el recargo por su cuenta.
          selectedOptions: item.selectedOptions,
          optionsLabel: item.optionsLabel
        }))
      };
      await axios.post(`${API_URL}/api/pos/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('success', 'Pedido guardado', `Pedido pendiente por $${getTotal().toFixed(2)} guardado correctamente.${selectedTable ? ` Mesa: ${selectedTable.name}` : ''}`);
      setCart([]);
      setCustomerName('');
      setSelectedTable(null);
      setOrderType('servir');
      setShowPaymentModal(false);
      onSaleComplete();
      loadProducts({ reset: true });
      loadTables();
    } catch (error) {
      const backendMsg = error.response?.data?.error;
      showAlert('error', 'Error al guardar pedido', backendMsg || 'No se pudo guardar el pedido pendiente. Verifica tu conexión e intenta de nuevo.');
    } finally {
      setSavingPending(false);
    }
  };

  // AbortController de la petición de productos en curso: si el cajero
  // sigue escribiendo o cambia de categoría antes de que responda el
  // servidor, se cancela la anterior para no pisar resultados fuera de orden.
  const productsAbortRef = React.useRef(null);

  // ⭐ FIX lentitud al cambiar de categoría: la paginación server-side (ver
  // comentario arriba) es correcta para no precargar todo el catálogo, pero
  // cada cambio de categoría disparaba SIEMPRE un round-trip al backend y,
  // mientras tanto, la grilla seguía mostrando los productos de la categoría
  // ANTERIOR (el swap solo pasaba al llegar la respuesta) — eso es lo que se
  // sentía como 1-2s de "colgado". Ahora se cachea en memoria la primera
  // página de cada categoría/búsqueda ya visitada en esta sesión: al volver
  // a una categoría ya vista, el cambio es instantáneo (se pinta el cache
  // de una vez) y de todas formas se revalida en segundo plano por si algo
  // cambió (nuevo producto, stock actualizado, etc). Solo la PRIMERA vez que
  // se visita una categoría se espera al servidor -- y ahí se muestra un
  // esqueleto de carga en vez de dejar pegados los productos de otra
  // categoría, que era confuso.
  const productsCacheRef = React.useRef({});
  const cacheKey = `${selectedCategory || ''}|${debouncedSearch}`;

  const loadProducts = async ({ reset = false } = {}) => {
    if (productsAbortRef.current) productsAbortRef.current.abort();
    const controller = new AbortController();
    productsAbortRef.current = controller;

    const nextPage = reset ? 1 : productsPage + 1;

    if (reset) {
      const cached = productsCacheRef.current[cacheKey];
      if (cached) {
        // Pintar de inmediato lo que ya sabemos de esta categoría mientras
        // se revalida en segundo plano (sin spinner: no hace falta, ya hay
        // algo correcto en pantalla).
        setProducts(cached.rows);
        setProductsPage(cached.page);
        setProductsTotal(cached.total);
        setProductsHasMore(cached.hasMore);
      } else {
        // Categoría nunca vista en esta sesión: no dejar los productos de
        // la categoría anterior en pantalla, mejor un esqueleto de carga.
        setProducts([]);
        setLoadingProducts(true);
      }
    } else {
      setLoadingProducts(true);
    }

    try {
      const params = { page: nextPage, limit: PRODUCTS_PAGE_SIZE };
      if (selectedCategory) params.category = selectedCategory;
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await axios.get(`${API_URL}/api/pos/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal: controller.signal
      });
      const { rows, page, total, hasMore } = response.data;
      setProducts(prev => (reset ? rows : [...prev, ...rows]));
      setProductsPage(page);
      setProductsTotal(total);
      setProductsHasMore(hasMore);
      if (reset) {
        productsCacheRef.current[cacheKey] = { rows, page, total, hasMore };
      }
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return;
      console.error('Error al cargar productos:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Precio efectivo del item según tipo de pedido
  const getItemPrice = (product) => {
    const surcharge = parseFloat(product.takeaway_surcharge) || 0;
    // ⭐ El recargo de las opciones elegidas (ej. "chuleta +$1") se suma acá para
    // que quede incluido en TODOS los cálculos de una sola vez: total del
    // carrito, subtotal por línea, recibo y comanda. El backend lo recalcula por
    // su cuenta al vender, así que este número es solo lo que se muestra.
    const optionsDelta = parseFloat(product.optionsPriceDelta) || 0;
    const base = parseFloat(product.price) + optionsDelta;
    return orderType === 'llevar' ? base + surcharge : base;
  };

  // ⭐ Bloqueo de venta sin caja abierta: se revisa en el ÚNICO punto de
  // entrada real al carrito (addToCart) -- tanto el click en un plato como
  // el escaneo de código de barras (handleBarcodeSubmit) pasan por acá, así
  // que basta este solo chequeo para cubrir ambos casos. Solo bloquea si
  // `requireOpenCashSession` está activado desde Configuración Y ya se sabe
  // con certeza que no hay caja abierta (`cashSessionOpen === false`) --
  // mientras el estado real todavía se está cargando (null), se deja pasar
  // para no bloquear por error durante el primer segundo tras entrar a Caja.
  const isSaleBlockedByClosedCash = () => requireOpenCashSession && cashSessionOpen === false;

  const addToCart = (product) => {
    if (isSaleBlockedByClosedCash()) {
      showAlert('warning', 'Caja cerrada', 'No puedes agregar productos ni vender mientras la caja esté cerrada. Ábrela primero con el botón "Caja Cerrada" arriba.');
      return;
    }
    // ⭐ Si el plato tiene grupos de opciones y todavía no se eligieron, se abre
    // el selector primero. `product.selectedOptions` viene puesto cuando la
    // llamada llega DESDE el selector, y ahí sí se agrega directo.
    if (productsWithOptions.has(product.id) && !product.selectedOptions) {
      setOptionsFor(product);
      return;
    }

    // ⭐ La clave de línea deja de ser el id del producto: dos menestras con
    // proteínas distintas son dos líneas distintas del carrito, no una con
    // cantidad 2. Sin esto, elegir "pollo" y después "carne" pisaría la primera
    // elección y la cocina recibiría dos platos iguales.
    const lineKey = product.lineKey || (product.selectedOptions
      ? `${product.id}::${JSON.stringify(product.selectedOptions)}`
      : String(product.id));

    const existing = cart.find(item => (item.lineKey || String(item.id)) === lineKey);
    if (product.use_stock) {
      // El stock se cuenta por PRODUCTO, no por línea: tres líneas distintas del
      // mismo plato siguen consumiendo del mismo stock.
      const currentQty = cart
        .filter(item => item.id === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);
      if (currentQty >= product.stock) {
        showAlert('warning', 'Stock insuficiente', `Solo hay ${product.stock} unidades disponibles de "${product.name}"`);
        return;
      }
    }
    if (existing) {
      setCart(cart.map(item =>
        (item.lineKey || String(item.id)) === lineKey ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, lineKey, quantity: 1 }]);
    }
  };

  // ⭐ Escaneo de código de barras: compatible con lectores USB genéricos,
  // que actúan como teclado (escriben el código y mandan Enter). Busca el
  // producto por código y lo agrega directo al carrito -- así el cajero no
  // interrumpe el flujo tocando la pantalla para cada producto empacado.
  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    const code = barcodeInput.trim();
    if (!code || scanningBarcode) return;
    setScanningBarcode(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/products/barcode/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToCart(res.data);
    } catch (error) {
      showAlert('warning', 'Producto no encontrado', `No hay ningún producto con el código "${code}".`);
    } finally {
      setBarcodeInput('');
      setScanningBarcode(false);
    }
  };

  // ⭐ Se opera por lineKey y no por id: dos líneas del mismo plato con opciones
  // distintas deben poder subir/bajar de cantidad y borrarse por separado.
  const updateQuantity = (lineKey, delta) => {
    setCart(cart.map(item => {
      if ((item.lineKey || String(item.id)) === lineKey) {
        const newQuantity = item.quantity + delta;
        if (item.use_stock && newQuantity > item.stock) {
          showAlert('warning', 'Stock insuficiente', `Solo hay ${item.stock} unidades disponibles`);
          return item;
        }
        return { ...item, quantity: Math.max(0, newQuantity) };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (lineKey) => {
    setCart(cart.filter(item => (item.lineKey || String(item.id)) !== lineKey));
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (getItemPrice(item) * item.quantity), 0);
  };

  const getTotalSurcharge = () => {
    if (orderType !== 'llevar') return 0;
    return cart.reduce((sum, item) => sum + ((parseFloat(item.takeaway_surcharge) || 0) * item.quantity), 0);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchTerm('');
    setCustomerSearchResults([]);
    setShowCustomerResults(false);
  };

  const openRegisterCustomer = (prefill = '') => {
    const trimmed = prefill.trim();
    const looksLikeCedula = /^\d+$/.test(trimmed);
    setRegisterForm({
      first_name: looksLikeCedula ? '' : trimmed,
      last_name: '',
      cedula: looksLikeCedula ? trimmed : '',
      phone: '',
      sector: '',
      email: '',
      document_type: '05',
      legal_name: '',
      fiscal_address: ''
    });
    setShowRegisterCustomer(true);
    setShowCustomerResults(false);
  };

  const handleRegisterCustomer = async () => {
    if (!registerForm.first_name.trim()) {
      showAlert('warning', 'Nombre requerido', 'El nombre del cliente es obligatorio.');
      return;
    }
    setRegisteringCustomer(true);
    try {
      const res = await axios.post(`${API_URL}/api/pos/customers`, registerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(res.data);
      setShowRegisterCustomer(false);
      setCustomerSearchTerm('');
      setCustomerSearchResults([]);
      showAlert('success', 'Cliente registrado', `${res.data.first_name} ${res.data.last_name || ''} fue registrado correctamente.`);
    } catch (error) {
      const backendMsg = error.response?.data?.error;
      showAlert('error', 'Error al registrar cliente', backendMsg || 'No se pudo registrar el cliente.');
    } finally {
      setRegisteringCustomer(false);
    }
  };

  const handlePayment = async () => {
    if (cart.length === 0) {
      showAlert('warning', 'Carrito vacío', 'Agrega productos al carrito antes de procesar el pago.');
      return;
    }
    if (billingType === 'factura' && !selectedCustomer) {
      showAlert('warning', 'Cliente requerido', 'Selecciona o registra un cliente para poder facturar.');
      return;
    }
    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived);
      if (!cashReceived || isNaN(received)) {
        showAlert('warning', 'Monto requerido', 'Por favor ingresa el monto recibido en efectivo.');
        return;
      }
      if (received < getTotal()) {
        showAlert('warning', 'Monto insuficiente', `El monto recibido ($${received.toFixed(2)}) es menor al total ($${getTotal().toFixed(2)})`);
        return;
      }
    }
    if (paymentMethod === 'mixed') {
      const total_paid = (parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0);
      if (total_paid < getTotal()) {
        showAlert('warning', 'Monto insuficiente', `El total pagado ($${total_paid.toFixed(2)}) es menor al total ($${getTotal().toFixed(2)})`);
        return;
      }
    }

    try {
      const saleData = {
        total: getTotal(),
        payment_method: paymentMethod,
        payment_status: 'paid',
        cash_received: paymentMethod === 'cash' ? parseFloat(cashReceived) : (paymentMethod === 'mixed' ? parseFloat(cashReceived) || 0 : null),
        transfer_amount: paymentMethod === 'transfer' ? getTotal() : (paymentMethod === 'mixed' ? parseFloat(transferAmount) || 0 : null),
        change_given: paymentMethod === 'cash' ? parseFloat(cashReceived) - getTotal() : (paymentMethod === 'mixed' ? Math.max(0, (parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0) - getTotal()) : null),
        order_type: orderType,
        customer_name: customerName.trim(),
        table_id: selectedTable?.id || null,
        billing_type: billingType,
        billing_confirmed: true,
        billing_data: billingType === 'factura' && selectedCustomer ? JSON.stringify(selectedCustomer) : null,
        customer_id: selectedCustomer?.id || null,
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: getItemPrice(item),
          quantity: item.quantity,
          use_stock: item.use_stock,
          // ⭐ El backend revalida esto y recalcula el recargo por su cuenta.
          selectedOptions: item.selectedOptions,
          optionsLabel: item.optionsLabel
        }))
      };

      const res = await axios.post(`${API_URL}/api/pos/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // ⭐ No se limpia el carrito/modal aqui: se difiere a onDone, que
      // ReciboManager ejecuta despues de que el usuario decide si imprime
      // el recibo o no. Asi el modal de cobros nunca se cierra antes de
      // preguntar por la impresion.
      setPendingReceipt({
        id: res.data?.id,
        items: cart.map(item => ({
          name: item.name,
          quantity: item.quantity,
          subtotal: getItemPrice(item) * item.quantity
        })),
        total: getTotal(),
        surcharge: getTotalSurcharge(),
        payment_method: paymentMethod,
        cash_received: saleData.cash_received,
        transfer_amount: saleData.transfer_amount,
        change_given: saleData.change_given,
        order_type: orderType,
        table_label: selectedTable?.name || null,
        customer_name: customerName.trim(),
        billing_type: billingType,
        billing_customer: billingType === 'factura' ? selectedCustomer : null,
        created_at: new Date().toISOString(),
        onDone: () => {
          setCart([]);
          setShowPaymentModal(false);
          setCashReceived('');
          setTransferAmount('');
          setShowChange(false);
          setCustomerName('');
          setSelectedTable(null);
          setOrderType('servir');
          setPendingReceipt(null);
          setSelectedCustomer(null);
          setBillingType('consumidor_final');
          setCustomerSearchTerm('');
          setCustomerSearchResults([]);
          onSaleComplete();
          loadProducts();
          loadTables();
        }
      });
    } catch (error) {
      console.error('Error al registrar venta:', error);
      const backendMsg = error.response?.data?.error;
      showAlert('error', 'Error al registrar venta', backendMsg || 'No se pudo registrar la venta. Intenta nuevamente.');
    }
  };

  // La categoría y el texto de búsqueda ya se resuelven en el servidor
  // (loadProducts); acá solo se oculta lo agotado, igual que antes.
  const filteredProducts = products.filter(p => !(p.use_stock && p.stock <= 0));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {customAlert && <CustomAlert {...customAlert} onClose={() => setCustomAlert(null)} compact zIndex={70} />}
      <ReciboManager pendingReceipt={pendingReceipt} businessName={clientData?.business_name} clientPhone={clientData?.phone} token={token} />

      {/* Panel de Productos */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg shadow-md p-4">

          {/* Tipo de pedido */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setOrderType('servir')}
              className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${orderType === 'servir' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Servir en mesa
            </button>
            <button
              onClick={() => setOrderType('llevar')}
              className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${orderType === 'llevar' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Para llevar
            </button>
          </div>


          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800">Productos</h2>
            <CierreCaja token={token} clientData={clientData} onSessionChange={(s) => setCashSessionOpen(!!s)} />
          </div>

          {/* Escaneo de código de barras */}
          <form onSubmit={handleBarcodeSubmit} className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="Escanear código de barras..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" disabled={scanningBarcode || !barcodeInput.trim()}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-900 disabled:opacity-50">
              Agregar
            </button>
          </form>

          {/* Buscar producto por nombre */}
          <div className="relative mb-4">
            <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar producto por nombre..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Selector de Categoría — antes eran pills siempre visibles con
              "Todos" precargando el catálogo completo; ahora es un botón que
              abre un selector y obliga a elegir explícitamente qué ver. */}
          <div className="mb-4">
            <button
              onClick={() => setShowCategoryPicker(true)}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-blue-50 text-blue-700 border-2 border-blue-200 hover:border-blue-400 transition-colors"
            >
              <span className="flex items-center gap-2">
                <LayoutGrid size={16} />
                {selectedCategory || 'Seleccionar categoría'}
              </span>
              <span className="text-xs font-normal text-blue-500">Cambiar</span>
            </button>
          </div>

          {/* Grid de Productos */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {!selectedCategory && !searchTerm ? (
              <div className="col-span-full text-center py-12 text-gray-400">
                <LayoutGrid size={40} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">Selecciona una categoría para ver sus productos</p>
                <button
                  onClick={() => setShowCategoryPicker(true)}
                  className="mt-3 text-blue-600 text-sm font-semibold hover:underline"
                >
                  Elegir categoría
                </button>
              </div>
            ) : filteredProducts.length === 0 && loadingProducts ? (
              // ⭐ Esqueleto de carga (solo la primera vez que se visita una
              // categoría/búsqueda, ver loadProducts): da feedback inmediato
              // en vez de una grilla en blanco que se siente "colgada".
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden animate-pulse">
                  <div className="w-full h-24 bg-gray-100" />
                  <div className="p-2.5 space-y-2">
                    <div className="h-3.5 bg-gray-100 rounded w-4/5" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))
            ) : filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-400">
                {(searchTerm || selectedCategory)
                  ? 'No hay productos que coincidan con la búsqueda.'
                  : 'No hay productos. Crea productos en la pestaña "Productos".'}
              </div>
            ) : (
              filteredProducts.map(product => {
                const effectivePrice = getItemPrice(product);
                const hasSurcharge = orderType === 'llevar' && (parseFloat(product.takeaway_surcharge) || 0) > 0;
                return (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className="bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:shadow-md transition text-left overflow-hidden"
                  >
                    {/* Imagen del producto */}
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-24 object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                    <div className="p-2.5">
                      <p className="font-semibold text-gray-800 text-sm leading-tight mb-0.5 line-clamp-2">{product.name}</p>
                      {product.description && (
                        <p className="text-xs text-gray-400 line-clamp-2 mb-1 leading-tight">{product.description}</p>
                      )}
                      <p className="text-lg font-bold text-blue-600">${effectivePrice.toFixed(2)}</p>
                      {hasSurcharge && (
                        <p className="text-xs text-orange-500">+${parseFloat(product.takeaway_surcharge).toFixed(2)} arancel</p>
                      )}
                      {product.use_stock ? (
                        <p className={`text-xs mt-0.5 ${product.stock <= 5 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                          Stock: {product.stock}
                        </p>
                      ) : (
                        <p className="text-xs mt-0.5 text-green-500 font-medium">∞ Sin límite de stock</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {productsHasMore && (
            <div className="text-center mt-4">
              <button
                onClick={() => loadProducts({ reset: false })}
                disabled={loadingProducts}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                {loadingProducts ? 'Cargando...' : `Cargar más (${products.length} de ${productsTotal})`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Carrito */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-lg shadow-md p-4 sticky top-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <ShoppingCart size={20} />
              Carrito
              {orderType === 'llevar' && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Para llevar</span>
              )}
            </h2>
            {cart.length > 0 && (
              <span className="bg-blue-600 text-white px-2.5 py-0.5 rounded-full text-sm font-bold">{cart.reduce((s, i) => s + i.quantity, 0)}</span>
            )}
          </div>

          {selectedTable && (
            <p className="text-xs text-blue-600 mb-1 bg-blue-50 rounded px-2 py-1 font-medium">
              <LayoutGrid className="w-3 h-3 inline mr-1" />Mesa: <strong>{selectedTable.name}</strong>
            </p>
          )}
          {/* Nombre del cliente — solo para Para Llevar */}
          {orderType === 'llevar' && (
            <div className="mb-3">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Nombre del cliente (opcional)"
                className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-orange-50"
              />
            </div>
          )}

          {cart.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <ShoppingCart size={40} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Carrito vacío</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-3 max-h-80 overflow-y-auto">
                {cart.map(item => {
                  const price = getItemPrice(item);
                  const surcharge = parseFloat(item.takeaway_surcharge) || 0;
                  const lineKey = item.lineKey || String(item.id);
                  return (
                    <div key={lineKey} className="border border-gray-200 rounded-lg p-2.5">
                      <div className="flex justify-between items-start mb-1.5">
                        <div className="flex-1 mr-2">
                          <p className="font-medium text-gray-800 text-sm leading-tight">{item.name}</p>
                          {/* ⭐ Las opciones elegidas se muestran bajo el nombre: es lo
                              único que distingue dos líneas del mismo plato. */}
                          {item.optionsLabel && (
                            <p className="text-[11px] text-blue-600 leading-tight mt-0.5">{item.optionsLabel}</p>
                          )}
                          {orderType === 'llevar' && surcharge > 0 && (
                            <p className="text-xs text-orange-500">${item.price.toFixed(2)} + ${surcharge.toFixed(2)}</p>
                          )}
                        </div>
                        <button onClick={() => removeFromCart(lineKey)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => updateQuantity(lineKey, -1)} className="bg-gray-200 hover:bg-gray-300 rounded p-0.5">
                            <Minus size={14} />
                          </button>
                          <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                          <button onClick={() => updateQuantity(lineKey, 1)} className="bg-gray-200 hover:bg-gray-300 rounded p-0.5">
                            <Plus size={14} />
                          </button>
                        </div>
                        <p className="font-bold text-blue-600 text-sm">${(price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Resumen totales */}
              <div className="border-t-2 border-gray-200 pt-3 mb-3 space-y-1">
                {getTotalSurcharge() > 0 && (
                  <>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Subtotal:</span>
                      <span>${(getTotal() - getTotalSurcharge()).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-orange-500">
                      <span>Arancel llevar:</span>
                      <span>+${getTotalSurcharge().toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-700">Total:</span>
                  <span className="text-2xl font-bold text-green-600">${getTotal().toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-2 mt-3">
                <button
                  onClick={() => {
                    // ⭐ Segunda barrera además de addToCart: cubre el caso raro
                    // de que la caja se haya cerrado desde OTRO dispositivo
                    // mientras este carrito ya tenía productos agregados.
                    if (isSaleBlockedByClosedCash()) {
                      showAlert('warning', 'Caja cerrada', 'No puedes procesar el pago mientras la caja esté cerrada. Ábrela primero con el botón "Caja Cerrada" arriba.');
                      return;
                    }
                    setShowPaymentModal(true);
                  }}
                  className="w-full bg-green-600 text-white py-2.5 rounded-lg font-bold hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <CreditCard size={18} />
                  Procesar Pago
                </button>
                <button
                  onClick={() => { setCart([]); setSelectedTable(null); }}
                  className="w-full bg-red-50 text-red-600 py-2 rounded-lg font-semibold hover:bg-red-100 text-sm"
                >
                  Limpiar Carrito
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Pago */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-gray-800">Procesar Pago</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="mb-5 bg-green-50 rounded-xl p-4 text-center">
              <p className="text-gray-500 text-sm mb-1">Total a pagar</p>
              <p className="text-4xl font-bold text-green-600">${getTotal().toFixed(2)}</p>
              {getTotalSurcharge() > 0 && (
                <p className="text-xs text-orange-500 mt-1">Incluye ${getTotalSurcharge().toFixed(2)} de arancel para llevar</p>
              )}
              {selectedTable && <p className="text-xs text-blue-600 mt-1">🪑 Mesa: {selectedTable.name}</p>}
              {customerName && <p className="text-xs text-gray-500 mt-1">Cliente: {customerName}</p>}
            </div>

            {/* Cliente — búsqueda/registro + tipo de comprobante (feature #9/#11) */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                <User className="w-3 h-3" /> Cliente
                <span className="text-xs font-normal text-gray-400">(opcional para Consumidor Final)</span>
              </p>

              {selectedCustomer ? (
                <div className="mb-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-blue-900">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                    {selectedCustomer.cedula && <p className="text-xs text-blue-600">Cédula: {selectedCustomer.cedula}</p>}
                  </div>
                  <button onClick={() => setSelectedCustomer(null)} className="text-blue-400 hover:text-blue-600">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="relative mb-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={customerSearchTerm}
                      onChange={(e) => { setCustomerSearchTerm(e.target.value); setShowCustomerResults(true); }}
                      onFocus={() => setShowCustomerResults(true)}
                      placeholder="Buscar por cédula o nombre..."
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {showCustomerResults && customerSearchTerm.trim() && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchingCustomer ? (
                        <p className="px-3 py-2 text-xs text-gray-400">Buscando...</p>
                      ) : customerSearchResults.length > 0 ? (
                        customerSearchResults.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            <p className="text-sm font-medium text-gray-800">{c.first_name} {c.last_name}</p>
                            {c.cedula && <p className="text-xs text-gray-400">Cédula: {c.cedula}</p>}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2">
                          <p className="text-xs text-gray-400 mb-1.5">Sin resultados.</p>
                          <button
                            type="button"
                            onClick={() => openRegisterCustomer(customerSearchTerm)}
                            className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700"
                          >
                            <UserPlus size={14} /> Registrar nuevo cliente
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!selectedCustomer && (
                <button
                  type="button"
                  onClick={() => openRegisterCustomer('')}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <UserPlus size={12} /> Registrar cliente nuevo
                </button>
              )}

              {/* Tipo de comprobante */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setBillingType('consumidor_final')}
                  className={`py-2 rounded-lg text-xs font-semibold border-2 transition-colors ${billingType === 'consumidor_final' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}
                >
                  Consumidor Final
                </button>
                <button
                  type="button"
                  onClick={() => setBillingType('factura')}
                  className={`py-2 rounded-lg text-xs font-semibold border-2 transition-colors ${billingType === 'factura' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}
                >
                  Factura
                </button>
              </div>

              {billingType === 'factura' && (
                selectedCustomer ? (
                  <div className="mt-3 bg-white border-2 border-indigo-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-3">Datos de facturación</p>
                    <div className="space-y-2.5">
                      <div>
                        <p className="text-[11px] text-gray-400 font-semibold uppercase">Nombre completo</p>
                        <p className="text-lg font-bold text-gray-900 leading-snug">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                      </div>
                      {selectedCustomer.cedula && (
                        <div>
                          <p className="text-[11px] text-gray-400 font-semibold uppercase">Cédula</p>
                          <p className="text-lg font-semibold text-gray-800">{selectedCustomer.cedula}</p>
                        </div>
                      )}
                      {selectedCustomer.phone && (
                        <div>
                          <p className="text-[11px] text-gray-400 font-semibold uppercase">Teléfono</p>
                          <p className="text-lg font-semibold text-gray-800">{selectedCustomer.phone}</p>
                        </div>
                      )}
                      {selectedCustomer.email && (
                        <div>
                          <p className="text-[11px] text-gray-400 font-semibold uppercase">Correo electrónico</p>
                          <p className="text-base font-semibold text-gray-800 break-all">{selectedCustomer.email}</p>
                        </div>
                      )}
                      {selectedCustomer.sector && (
                        <div>
                          <p className="text-[11px] text-gray-400 font-semibold uppercase">Sector</p>
                          <p className="text-lg font-semibold text-gray-800">{selectedCustomer.sector}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                    Selecciona o registra un cliente para poder facturar.
                  </p>
                )
              )}
            </div>

            {/* Asignar mesa — 3 botones compactos que abren modales */}
            {orderType === 'servir' && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                  <LayoutGrid className="w-3 h-3" /> Asignar mesa
                  <span className="text-xs font-normal text-gray-400">(opcional)</span>
                </p>

                {/* Indicador de mesa seleccionada */}
                {selectedTable && (
                  <div className="mb-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    <p className="text-xs font-semibold text-blue-700">
                      Mesa asignada: <span className="text-blue-900">{selectedTable.name}</span>
                      {tables.find(t => t.id === selectedTable.id)?.status === 'occupied' && (
                        <span className="ml-1 text-orange-600">(comanda adicional)</span>
                      )}
                    </p>
                    <button onClick={() => setSelectedTable(null)} className="text-blue-400 hover:text-blue-600">
                      <X size={14} />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setSelectedTable(null)}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition-colors text-center ${!selectedTable ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}
                  >
                    Sin mesa
                  </button>
                  <button
                    onClick={() => setShowTablesModal('available')}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition-colors text-center ${selectedTable && tables.find(t => t.id === selectedTable.id)?.status === 'available'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-green-50 text-green-700 border-green-300 hover:border-green-500 hover:bg-green-100'}`}
                  >
                    Disponibles ({tables.filter(t => t.status === 'available').length})
                  </button>
                  <button
                    onClick={() => setShowTablesModal('occupied')}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition-colors text-center ${selectedTable && tables.find(t => t.id === selectedTable.id)?.status === 'occupied'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-orange-50 text-orange-700 border-orange-300 hover:border-orange-500 hover:bg-orange-100'}`}
                  >
                    Ocupadas ({tables.filter(t => t.status === 'occupied').length})
                  </button>
                </div>
              </div>
            )}

            {/* Dejar pendiente de pago — compacto, no cierra modal */}
            <button
              onClick={saveAsPending}
              disabled={savingPending}
              className="w-full mb-4 border border-orange-300 text-orange-600 py-1.5 rounded-lg font-semibold hover:bg-orange-50 flex items-center justify-center gap-1.5 disabled:opacity-60 text-xs"
            >
              <Clock size={14} />
              {savingPending ? 'Guardando...' : 'Dejar pendiente de pago'}
            </button>

            {/* Método de pago */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Método de Pago</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'cash', label: 'Efectivo', Icon: DollarSign, color: 'green' },
                  { id: 'transfer', label: 'Transferencia', Icon: CreditCard, color: 'blue' },
                  { id: 'mixed', label: 'Mixto', Icon: null, color: 'purple' },
                ].map(({ id, label, Icon: BtnIcon, color }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setPaymentMethod(id); setCashReceived(''); setTransferAmount(''); setShowChange(false); }}
                    className={`p-3 border-2 rounded-xl transition ${paymentMethod === id ? `border-${color}-500 bg-${color}-50` : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    {BtnIcon ? <BtnIcon className={`mx-auto mb-1 text-${color}-600`} size={24} /> : (
                      <div className="flex items-center justify-center gap-0.5 mb-1">
                        <DollarSign className="text-purple-600" size={16} />
                        <Plus className="text-purple-600" size={12} />
                        <CreditCard className="text-purple-600" size={16} />
                      </div>
                    )}
                    <p className="font-semibold text-center text-xs">{label}</p>
                  </button>
                ))}
              </div>
            </div>

            {paymentMethod === 'cash' && (
              <div className="mb-5 bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto recibido en efectivo *</label>
                <input
                  type="number" step="0.01" value={cashReceived}
                  onChange={(e) => { setCashReceived(e.target.value); setShowChange(!!e.target.value); }}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 mb-3"
                  placeholder="0.00" autoFocus
                />
                {showChange && cashReceived && parseFloat(cashReceived) >= getTotal() && (
                  <div className="bg-white border-2 border-green-400 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Cambio a entregar:</p>
                    <p className="text-3xl font-bold text-green-600">${(parseFloat(cashReceived) - getTotal()).toFixed(2)}</p>
                  </div>
                )}
                {cashReceived && parseFloat(cashReceived) < getTotal() && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-2 text-center">
                    <p className="text-sm text-red-700">Falta: ${(getTotal() - parseFloat(cashReceived)).toFixed(2)}</p>
                  </div>
                )}
              </div>
            )}

            {paymentMethod === 'mixed' && (
              <div className="mb-5 bg-purple-50 border-2 border-purple-200 rounded-xl p-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto en efectivo</label>
                  <input type="number" step="0.01" value={cashReceived}
                    onChange={(e) => { setCashReceived(e.target.value); setShowChange((parseFloat(e.target.value) || 0) + (parseFloat(transferAmount) || 0) >= getTotal()); }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00" autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto en transferencia</label>
                  <input type="number" step="0.01" value={transferAmount}
                    onChange={(e) => { setTransferAmount(e.target.value); setShowChange((parseFloat(cashReceived) || 0) + (parseFloat(e.target.value) || 0) >= getTotal()); }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00" />
                </div>
                <div className="bg-white border border-purple-200 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Total:</span><span className="font-bold">${getTotal().toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Pagado:</span><span className="font-bold text-purple-700">${((parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0)).toFixed(2)}</span></div>
                  {showChange && <div className="flex justify-between text-sm border-t pt-1"><span className="text-gray-500">Cambio:</span><span className="font-bold text-green-600">${Math.max(0, (parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0) - getTotal()).toFixed(2)}</span></div>}
                  {(cashReceived || transferAmount) && ((parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0)) < getTotal() && (
                    <p className="text-xs text-red-600 text-center">Falta: ${(getTotal() - ((parseFloat(cashReceived) || 0) + (parseFloat(transferAmount) || 0))).toFixed(2)}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold">
                Cancelar
              </button>
              <button onClick={handlePayment} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold">
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Registro de Cliente */}
      {showRegisterCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[65] p-4" onClick={() => setShowRegisterCustomer(false)}>
          <div className="bg-white rounded-xl max-w-sm w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <UserPlus size={20} /> Registrar Cliente
              </h3>
              <button onClick={() => setShowRegisterCustomer(false)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre *</label>
                <input type="text" value={registerForm.first_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Apellido</label>
                <input type="text" value={registerForm.last_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de identificación</label>
                <select value={registerForm.document_type} onChange={(e) => setRegisterForm({ ...registerForm, document_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="05">Cédula</option><option value="04">RUC</option><option value="06">Pasaporte</option><option value="08">Identificación del exterior</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Número de identificación</label>
                <input type="text" value={registerForm.cedula}
                  onChange={(e) => setRegisterForm({ ...registerForm, cedula: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Razón social / nombre para factura</label>
                <input type="text" value={registerForm.legal_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, legal_name: e.target.value })}
                  placeholder="Si queda vacío se usa nombre y apellido"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Teléfono</label>
                <input type="text" value={registerForm.phone}
                  onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Sector de residencia</label>
                <input type="text" value={registerForm.sector}
                  onChange={(e) => setRegisterForm({ ...registerForm, sector: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Dirección para factura</label>
                <input type="text" value={registerForm.fiscal_address}
                  onChange={(e) => setRegisterForm({ ...registerForm, fiscal_address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Correo electrónico</label>
                <input type="email" value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowRegisterCustomer(false)} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold text-sm">
                Cancelar
              </button>
              <button onClick={handleRegisterCustomer} disabled={registeringCustomer} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold text-sm disabled:opacity-60">
                {registeringCustomer ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selector de Categoría */}
      {showCategoryPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setShowCategoryPicker(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <LayoutGrid size={20} />
                Seleccionar Categoría
              </h3>
              <button onClick={() => setShowCategoryPicker(false)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            {categories.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <LayoutGrid size={36} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No hay categorías. Crea una en la pestaña "Categorías".</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => { setSelectedCategory(cat); setShowCategoryPicker(false); }}
                    className={`p-3 rounded-xl border-2 transition-all text-center font-semibold text-sm ${selectedCategory === cat
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-blue-50 text-blue-800 border-blue-200 hover:border-blue-500 hover:shadow-md'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowCategoryPicker(false)}
              className="w-full mt-4 py-2 border border-gray-300 rounded-lg text-gray-600 font-semibold hover:bg-gray-50 text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Modal de Mesas (Disponibles u Ocupadas) */}
      {showTablesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setShowTablesModal(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <LayoutGrid size={20} />
                {showTablesModal === 'available' ? 'Mesas Disponibles' : 'Mesas Ocupadas'}
              </h3>
              <button onClick={() => setShowTablesModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            {showTablesModal === 'available' && (
              <>
                {tables.filter(t => t.status === 'available').length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <LayoutGrid size={36} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No hay mesas disponibles</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {tables.filter(t => t.status === 'available').map(table => {
                      const isSelected = selectedTable?.id === table.id;
                      return (
                        <button key={table.id}
                          onClick={() => { setSelectedTable(table); setShowTablesModal(null); }}
                          className={`p-3 rounded-xl border-2 transition-all text-center ${isSelected
                            ? 'bg-green-600 text-white border-green-600 shadow-md'
                            : 'bg-green-50 text-green-800 border-green-200 hover:border-green-500 hover:shadow-md'}`}>
                          <LayoutGrid size={20} className="mx-auto mb-1.5 opacity-70" />
                          <p className="font-bold text-sm">{table.name}</p>
                          <p className="text-xs opacity-70 mt-0.5">Libre</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {showTablesModal === 'occupied' && (
              <>
                {tables.filter(t => t.status === 'occupied').length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <LayoutGrid size={36} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No hay mesas ocupadas</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-orange-600 mb-3 bg-orange-50 rounded-lg px-3 py-2">
                      Al seleccionar una mesa ocupada, se agregará una nueva comanda a la cuenta existente.
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {tables.filter(t => t.status === 'occupied').map(table => {
                        const isSelected = selectedTable?.id === table.id;
                        return (
                          <button key={table.id}
                            onClick={() => { setSelectedTable(table); setShowTablesModal(null); }}
                            className={`p-3 rounded-xl border-2 transition-all text-center ${isSelected
                              ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                              : 'bg-orange-50 text-orange-800 border-orange-200 hover:border-orange-500 hover:shadow-md'}`}>
                            <LayoutGrid size={20} className="mx-auto mb-1.5 opacity-70" />
                            <p className="font-bold text-sm">{table.name}</p>
                            <p className="text-xs opacity-70 mt-0.5">{table.order_count || 0} comanda{(table.order_count || 0) !== 1 ? 's' : ''}</p>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}

            <button
              onClick={() => setShowTablesModal(null)}
              className="w-full mt-4 py-2 border border-gray-300 rounded-lg text-gray-600 font-semibold hover:bg-gray-50 text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
      {/* ⭐ Selector de opciones del plato. Se monta al final para que quede por
          encima de todo, y se desmonta al elegir o cancelar. */}
      {optionsFor && (
        <ProductOptionsPicker
          product={optionsFor}
          token={token}
          onCancel={() => setOptionsFor(null)}
          onConfirm={(sel) => {
            const chosen = optionsFor;
            setOptionsFor(null);
            addToCart({ ...chosen, ...sel });
          }}
        />
      )}
    </div>
  );
};

export default CajaVentas;
